/**
 * «Доля отчётов» — ОДНО определение на `/`, `/overview` и «К совещанию»
 * (agroai-0yt.2).
 *
 * Раньше три экрана показывали три числа под одним смыслом: главная — все
 * обязательства цикла на дату своего фильтра (61,7 %), реестр района — только
 * открытые окна из файла работ на глобальную дату (68,7 %), а сводка к
 * совещанию — дату своего фильтра или памяти сессии. Разные знаменатели и
 * разные даты читались как разные данные.
 *
 * Определение: отчитавшиеся обязательства / все обязательства активного цикла
 * на дату среза; обе величины — ответ сервера `/season/picture`
 * (`obligations.reported` / `obligations.total`) на одну дату и один флаг демо.
 */
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';
import { countOf, dateShort, num, pctAxis } from './format';

const m = defineMessages({
  reportShare: {
    id: 'common.ui.reportShare.label',
    defaultMessage: 'Доля отчётов',
    description: 'Подпись KPI на главной, обзоре и «К совещанию»: какая доля работ получила отчёт (когда сервер не разделил «выполнено» и «в процессе»)',
  },
  doneShare: {
    id: 'common.ui.reportShare.doneLabel',
    defaultMessage: 'Выполнено',
    description: 'Подпись KPI: доля работ, по которым внесён отчёт «выполнено»; коротко',
  },
  cycleUnit: {
    id: 'common.ui.reportShare.cycleUnit',
    defaultMessage: 'агроцикла',
    description: 'Слово в родительном падеже после числа работ в подписи KPI «26 144 из 42 365 работ агроцикла»; по умолчанию',
  },
  worksOnFields: {
    id: 'common.ui.reportShare.worksOnFields',
    defaultMessage: '{count, plural, one {# работа на поле} few {# работы на поле} many {# работ на полях} other {# работы на поле}}',
    description: 'Число работ на полях с единицей, для знаменателя «26 144 из 42 365 работ на полях агроцикла»',
  },
  note: {
    id: 'common.ui.reportShare.note',
    defaultMessage: '{reported} из {total} {unit}',
    description: 'Подпись под KPI «Доля отчётов»; {reported} — число, {total} — число с единицей («42 365 работ на полях»), {unit} — чей счёт («агроцикла»)',
  },
  noteAsOf: {
    id: 'common.ui.reportShare.noteAsOf',
    defaultMessage: '{note} · на {date}',
    description: 'Подпись под KPI с датой среза в конце; {note} — готовая подпись, {date} — дата («3 окт 2026»)',
  },
});

export function reportShareLabel(): string {
  return tr(m.reportShare);
}

/**
 * «Выполнено» (agroai-6al.35, журнал №290): обязательства с отчётом
 * «выполнено» / все обязательства цикла (`obligations.done / total`, метрика
 * `reports.done_pct`). Отчёт «в процессе» — работа начата, не закончена: в
 * числитель не входит. Прежняя «Доля отчётов» остаётся подписью для выдач,
 * где сервер степень выполнения не считал.
 */
export function doneShareLabel(): string {
  return tr(m.doneShare);
}

export function reportShareValue(reported: number, total: number): string {
  return total > 0 ? pctAxis((100 * reported) / total) : '—';
}

/** «26 144 из 42 365 работ на полях агроцикла · на 3 окт 2026». */
export function reportShareNote(reported: number, total: number, asOf: string | null, unit: string = tr(m.cycleUnit)): string {
  const base = tr(m.note, { reported: num(reported), total: countOf(total, m.worksOnFields), unit });
  return asOf ? tr(m.noteAsOf, { note: base, date: dateShort(asOf) }) : base;
}
